import { Platform } from "react-native";
import * as Sharing from "expo-sharing";

/**
 * Shared image share/save helpers, factored out of the Dashboard progress
 * poster's inline logic so the Profile Vibe Card can reuse the same
 * cross-platform behaviour without touching that feature. Purely local — no
 * network, no provider calls.
 */

type WebShareNavigator = Navigator & {
  canShare?: (data?: { files?: File[]; title?: string; text?: string }) => boolean;
  share?: (data?: { files?: File[]; title?: string; text?: string }) => Promise<void>;
};

function downloadDataUri(filename: string, dataUri: string): void {
  if (Platform.OS !== "web" || typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = dataUri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function shareWebImage(
  dataUri: string,
  filename: string,
  title: string,
  text: string,
): Promise<boolean> {
  if (Platform.OS !== "web" || typeof navigator === "undefined") return false;
  const nav = navigator as WebShareNavigator;
  if (!nav.share) return false;

  const response = await fetch(dataUri);
  const blob = await response.blob();
  const file = new File([blob], filename, { type: "image/png" });
  const payload = { files: [file], title, text };
  if (nav.canShare && !nav.canShare(payload)) return false;
  await nav.share(payload);
  return true;
}

/**
 * Share a captured PNG. Web uses the Web Share API (falling back to a
 * download); native uses the OS share sheet.
 */
export async function shareImage(
  uri: string,
  filename: string,
  opts: { dialogTitle: string; text?: string },
): Promise<void> {
  if (Platform.OS === "web") {
    const shared = await shareWebImage(uri, filename, opts.dialogTitle, opts.text ?? "");
    if (!shared) downloadDataUri(filename, uri);
    return;
  }
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, {
      dialogTitle: opts.dialogTitle,
      mimeType: "image/png",
      UTI: "public.png",
    });
  }
}

/**
 * Save a captured PNG to the device. Web downloads the file directly; native
 * routes through the OS share sheet (the sanctioned "Save Image" / "Save to
 * Files" path) so no extra media-library dependency or permission is needed.
 */
export async function saveImage(
  uri: string,
  filename: string,
  opts: { dialogTitle: string },
): Promise<void> {
  if (Platform.OS === "web") {
    downloadDataUri(filename, uri);
    return;
  }
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, {
      dialogTitle: opts.dialogTitle,
      mimeType: "image/png",
      UTI: "public.png",
    });
  }
}
